// Iter 92 — SMPTE timecode for the clip request workflow (mirrors backend/timecode.py).
export const ASSUMED_FPS = 23.976;
const NTSC = [[23.976, 24000 / 1001], [29.97, 30000 / 1001], [59.94, 60000 / 1001]];

export function exactFps(fps) {
  const hit = NTSC.find(([k]) => Math.abs(fps - k) < 0.01);
  return hit ? hit[1] : fps;
}

// { fps, assumed } — unknown/zero frame rate falls back to 23.976 (flagged).
export function resolveFps(frameRate) {
  const fr = Number(frameRate) || 0;
  return fr > 0 ? { fps: Math.round(fr * 1000) / 1000, assumed: false } : { fps: ASSUMED_FPS, assumed: true };
}

export const isDropFrame = (fps) => Math.abs(fps - 29.97) < 0.01 || Math.abs(fps - 59.94) < 0.01;
const dropCount = (base) => (base === 30 ? 2 : 4);

export const fpsLabel = ({ fps, assumed }) => (assumed ? `assumed ${fps} fps` : `${fps} fps`);
export const secToFrame = (sec, fps) => Math.max(0, Math.round((Number(sec) || 0) * exactFps(fps)));
export const frameToSec = (frame, fps) => frame / exactFps(fps);
// Playhead → the frame currently on screen (floor, tolerant of float noise).
export const playheadFrame = (sec, fps) => Math.max(0, Math.floor((Number(sec) || 0) * exactFps(fps) + 1e-6));

const p2 = (n) => String(n).padStart(2, "0");

export function frameToTc(frame, fps) {
  const base = Math.round(fps);
  const df = isDropFrame(fps);
  let n = Math.max(0, Math.floor(frame));
  if (df) {
    const drop = dropCount(base);
    const per10 = base * 600 - drop * 9;
    const perMin = base * 60 - drop;
    const d = Math.floor(n / per10);
    const m = n % per10;
    n += drop * 9 * d + (m > drop ? drop * Math.floor((m - drop) / perMin) : 0);
  }
  const ff = n % base;
  const total = Math.floor(n / base);
  return `${p2(Math.floor(total / 3600))}:${p2(Math.floor(total / 60) % 60)}:${p2(total % 60)}${df ? ";" : ":"}${p2(ff)}`;
}

// "HH:MM:SS:FF" (or ;FF) → frame number, or { error } when invalid.
export function tcToFrame(str, fps) {
  const m = String(str || "").trim().match(/^(\d{1,2}):(\d{2}):(\d{2})[:;.](\d{2})$/);
  if (!m) return { error: "Use HH:MM:SS:FF" };
  const [hh, mm, ss, ff] = m.slice(1).map(Number);
  const base = Math.round(fps);
  if (mm > 59 || ss > 59) return { error: "Minutes and seconds must be under 60" };
  if (ff >= base) return { error: `Frames must be under ${base}` };
  let n = (hh * 3600 + mm * 60 + ss) * base + ff;
  if (isDropFrame(fps)) {
    const drop = dropCount(base);
    if (ss === 0 && mm % 10 !== 0 && ff < drop) return { error: `Frames ;00–;0${drop - 1} are skipped at this minute` };
    const totalMin = hh * 60 + mm;
    n -= drop * (totalMin - Math.floor(totalMin / 10));
  }
  return { frame: n };
}

// Legacy (pre-frame-rate) clips: second-level with tenths, e.g. 0:12.5.
export function fmtSecTenths(sec) {
  const s = Math.max(0, Number(sec) || 0);
  const m = Math.floor(s / 60);
  return `${m}:${(s - m * 60).toFixed(1).padStart(4, "0")}`;
}

// Display strings for a stored clip; frame-based when the clip carries a frame rate.
export function clipTimecodes(clip) {
  const fr = clip.frame_rate ?? clip.frameRate;
  const inF = clip.in_frame ?? clip.inFrame;
  const outF = clip.out_frame ?? clip.outFrame;
  if (fr && inF != null && outF != null) {
    const assumed = !!(clip.frame_rate_assumed ?? clip.frameRateAssumed);
    return {
      inTc: frameToTc(inF, fr),
      outTc: frameToTc(outF, fr),
      length: `${outF - inF} frames`,
      fpsText: fpsLabel({ fps: fr, assumed }),
    };
  }
  const i = clip.in_sec ?? clip.inSec;
  const o = clip.out_sec ?? clip.outSec;
  return { inTc: fmtSecTenths(i), outTc: fmtSecTenths(o), length: `Δ ${fmtSecTenths(Math.max(0, o - i))}`, fpsText: null };
}
